import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Snackbar, Table } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MainCard from './MainCard';
import { visuallyHidden } from '@mui/utils';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';
import {Grid } from '@material-ui/core';
import axios from "../../components/axios";
import MuiAlert from '@mui/material/Alert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';


const StickyHeadTwoButtonTable = ({ rows, columns, handleDownload, handleRedirect, exportURL, fullQuery, showSummary,  handleSummary, showShare, handleRelease, showRelease, shareAPI, handleDelete }) => {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const styleExportButton = {
    backgroundColor: "#406599",
    color: "#fff",
    borderRadius: "5px",
    boxSizing: "border-box",
    width: "110px",
    height: "40px",
    fontFamily: '"Roboto", "sans-serif"',
    fontSize: "13px",
    marginBottom:'10px',
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const descendingComparator = React.useCallback((a, b, orderBy) => {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
  }, []);

  const getComparator = React.useCallback(() => {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
  }, [descendingComparator,order,orderBy]);

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)),
    [rows, getComparator, order, orderBy],
  );

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const handleExportData = () => {
      axios.post(exportURL,fullQuery,{withCredentials: true})
      .then(response => {
          var fileURL = window.URL.createObjectURL(new Blob([response.data]));
          var fileLink = document.createElement('a');
          fileLink.href = fileURL;
          fileLink.setAttribute('download', 'full_data.csv');
          document.body.appendChild(fileLink);
          fileLink.click();
      }).catch(function(err){
        if (!err.response){
          alert(err);
        }else{
          if (err.response.status === 401){
            navigate("/");
          }else{
            console.log('error.response.status: ', err.response.status);
            console.log('error.response.status: ', err.response.data.message);
            alert(err.response.data.message);
          }
        }
      })
  };

  const handleShareLink = () => {
    axios.post("/peloton/v1/automation/TOTAL/"+shareAPI,fullQuery,{withCredentials: true})
      .then(response => {
        if (response.data.message === "success") {
          navigator.clipboard.writeText(response.data.url)
        }else{
          alert(response.data.message);
        }
      }).catch(function(err){
        if (!err.response){
          alert(err);
        }else{
          if (err.response.status === 401){
            navigate("/");
          }else{
            console.log('error.response.status: ', err.response.status);
            console.log('error.response.status: ', err.response.data.message);
            alert(err.response.data.message);
          }
        }
      })
    setOpen(true)
  };

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  return (
    <MainCard >
    <Grid container justifyContent="space-around" >
    { showShare && <Button aria-modal style={styleExportButton} onClick={handleExportData}>Export Data</Button>}
    { showSummary && <Button aria-modal style={styleExportButton} onClick={handleSummary}>Summary</Button>}
    { showShare && <Button aria-modal style={styleExportButton} onClick={handleShareLink}>Share</Button>}
    { showRelease && <Button aria-modal style={styleExportButton} onClick={handleRelease}>Release notes</Button>}
    <Snackbar open={open} autoHideDuration={6000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleClose} severity="info" sx={{ width: '100%' }}>
          Link copy to clipboard!
        </Alert>
      </Snackbar>
    </Grid>
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{ minWidth: column.minWidth, bgcolor: "#406599", color:"white" }}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={createSortHandler(column.id)}
                  >
                    {column.label}
                    {orderBy === column.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                const uuid = row.uuid;
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row.uuid}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align}>
                        {typeof value === 'boolean'?
                          (value === false ? 'False':'True'):
                          (column.action?
                            (<div><Button onClick={() => handleDownload(uuid)}><CloudDownloadIcon sx={{ color: "#406599",fontSize: 20 }}/></Button></div>):
                            (column.view?(<div><Button onClick={() => handleRedirect(uuid)}><OpenInNewIcon sx={{ color: "#406599",fontSize: 20 }}/></Button></div>):
                              (column.ignore?(<div><Button onClick={() => handleDelete(row)}><VisibilityOffIcon sx={{ color: "#406599",fontSize: 20 }}/></Button></div>):(value))))}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ bgcolor: "#406599", color:"white" }}
      />
    </Paper>
    </MainCard>
  );
}

export default StickyHeadTwoButtonTable;
